import { type createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
	downloadBlob,
	fulfilledWithSettingsMatcher,
	isApiFulfilledAction,
	type SettingsAction,
	showToast,
	updatePersistor,
} from 'BreetHelpers';
import {
	apiSlice,
	type AppDispatch,
	clearCountDownStartTimeArray,
	closeModals,
	logUserIn,
	logUserOut,
	resetExportDates,
	type RootState,
	setAuthSettings,
	setBusiness,
	setCountDownStartTimeArray,
	setFcmToken,
	setIs2faAlert,
	setIsApprovedForLive,
	setIsFiatBalanceHidden,
	setShowSecretKey,
	setTargetEnvironment,
	setThemeMode,
} from 'BreetRedux';

export function appListeners(mw: ReturnType<typeof createListenerMiddleware>) {
	const startAppListening = mw.startListening.withTypes<RootState, AppDispatch>();

	startAppListening({
		predicate: isPersistenceAction,
		effect: (_, api) => {
			const state = api.getState();
			updatePersistor({
				countDownStartTimeArray: state.app.countDownStartTimeArray,
				themeMode: state.app.themeMode,
				isUserLoggedIn: state.app.isUserLoggedIn,
				lastVisited: state.app.lastVisited,
				fcmToken: state.app.fcmToken,
				isFiatBalanceHidden: state.app.isFiatBalanceHidden,
				showSecretKey: state.app.showSecretKey,
				is2faAlert: state.app.is2faAlert,
				targetEnvironment: state.app.targetEnvironment,
				isApprovedForLive: state.app.isApprovedForLive,
				business: state.app.business,
			});
		},
	});

	startAppListening({
		predicate: fulfilledWithSettingsMatcher,
		effect: (action, api) => {
			const { payload } = action as unknown as SettingsAction;
			if (payload.meta?.settings?.fxRates) {
				api.dispatch(setAuthSettings(payload.meta.settings));
			}
		},
	});

	startAppListening({
		actionCreator: setTargetEnvironment,
		effect: (_, api) => {
			api.dispatch(apiSlice.util.resetApiState());
		},
	});

	// Single teardown point for every logout path (deliberate, session-expiry, direct /getlost):
	// close any open modal and drop the cached API state. State reset lives in the logUserOut reducer.
	startAppListening({
		actionCreator: logUserOut,
		effect: (_, api) => {
			api.dispatch(closeModals());
			api.dispatch(apiSlice.util.resetApiState());
		},
	});

	startAppListening({
		predicate: (action) => {
			if (!isApiFulfilledAction(action)) return false;
			const { payload, meta } = action as { payload?: unknown; meta?: { arg?: { endpointName?: string } } };
			const endpointName = meta?.arg?.endpointName;
			return payload instanceof Blob && !!endpointName && BLOB_EXPORT_ENDPOINTS.has(endpointName);
		},
		effect: (action, api) => {
			const blob = (action as unknown as { payload: Blob }).payload;
			if (blob.size === 0) {
				showToast('error', 'No data found for the selected date range.');
			} else {
				downloadBlob(blob, `breet-export-${new Date().toISOString().slice(0, 10)}.csv`);
			}
			api.dispatch(resetExportDates());
		},
	});
}

const BLOB_EXPORT_ENDPOINTS = new Set<string>([
	'getTransactionsEditServices',
	'getTransactionsSellServices',
	'getTradesSubAccountWalletAddresses',
	'getWebhookLogs',
]);

const isPersistenceAction = isAnyOf(
	clearCountDownStartTimeArray,
	setCountDownStartTimeArray,
	setThemeMode,
	setIsFiatBalanceHidden,
	setFcmToken,
	setShowSecretKey,
	logUserIn,
	logUserOut,
	setIs2faAlert,
	setTargetEnvironment,
	setIsApprovedForLive,
	setBusiness
);
